import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Banknote,
  BarChart3,
  Building2,
  Calendar,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  Home,
  ListTree,
  Package,
  RefreshCw,
  Timer,
  Store,
  Tag,
  Truck,
  User,
} from "lucide-react";
import { FilterBar } from "@/components/ui/FilterBar";
import { Button } from "@/components/ui/Button";
import type { MultiSelectOption } from "@/components/ui/MultiSelect";
import { useModules } from "@/context/ModulesContext";
import { ROUTES } from "@/config/constants";
import { formatKanbanCountdown, useKanbanAutoRefresh } from "@/hooks/useKanbanAutoRefresh";
import { formatCurrency, formatIsoDateBr, formatNumber } from "@/utils/format";
import {
  type CotacaoCard,
  type KanbanComprasData,
  type PedidoCard,
  type SolicitacaoCard,
  effectiveSolicitacaoCostCenterId,
  buildSolicitacaoObraLine,
  labelPedidoSiengeStatus,
} from "@/services/suprimentos";
import { cn } from "@/utils/cn";
import { SolicitacaoItensModal } from "./SolicitacaoItensModal";
import { CotacaoItensModal } from "./CotacaoItensModal";
import { PedidoItensModal } from "./PedidoItensModal";
import { filterKanbanComprasData } from "./kanbanBoardFilter";
import { findKambarCostCenterIds, kambarFilterLabel } from "./kanbanKambar";

/** Mapa id → rótulo completo (ex.: código – nome), com sinônimos numéricos e código no label. */
function buildCostCenterLookup(options: MultiSelectOption[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const o of options) {
    const val = String(o.value ?? "").trim();
    const label = String(o.label ?? "").trim();
    if (!val || !label) continue;
    m.set(val, label);
    const n = Number(val);
    if (!Number.isNaN(n)) m.set(String(n), label);
    for (const sep of [" – ", " - ", "–", "-"]) {
      const i = label.indexOf(sep);
      if (i > 0) {
        const code = label.slice(0, i).trim();
        if (code) {
          m.set(code, label);
          const cn = Number(code);
          if (!Number.isNaN(cn)) m.set(String(cn), label);
        }
        break;
      }
    }
  }
  return m;
}

function resolveCostCenterDisplay(
  costCenterId: string,
  costCenterName: string,
  lookup: Map<string, string>
): string {
  const name = (costCenterName ?? "").trim();
  if (name) return name;
  const id = (costCenterId ?? "").trim();
  if (!id) return "";
  if (lookup.has(id)) return lookup.get(id)!;
  const num = Number(id);
  if (!Number.isNaN(num)) {
    const asS = String(num);
    if (lookup.has(asS)) return lookup.get(asS)!;
  }
  const stripped = id.replace(/^0+/, "") || "0";
  if (stripped !== id && lookup.has(stripped)) return lookup.get(stripped)!;
  if (lookup.has(stripped)) return lookup.get(stripped)!;
  return "";
}

function enrichKanbanData(
  data: KanbanComprasData,
  ccOptions: MultiSelectOption[],
  ccNameMap: Map<string, string>
): KanbanComprasData {
  const lookup = buildCostCenterLookup(ccOptions);
  const patchCc = (id: string, name: string) => {
    const fromResolve = resolveCostCenterDisplay(id, name, lookup).trim();
    if (fromResolve) return fromResolve;
    const idt = (id ?? "").trim();
    if (idt) {
      if (ccNameMap.has(idt)) return ccNameMap.get(idt)!;
      const num = Number(idt);
      if (!Number.isNaN(num) && ccNameMap.has(String(num))) return ccNameMap.get(String(num))!;
      const stripped = idt.replace(/^0+/, "") || "0";
      if (ccNameMap.has(stripped)) return ccNameMap.get(stripped)!;
    }
    return (name ?? "").trim();
  };

  return {
    solPendente: data.solPendente.map((c) => ({
      ...c,
      costCenterName: patchCc(effectiveSolicitacaoCostCenterId(c), c.costCenterName),
    })),
    solAprovada: data.solAprovada.map((c) => ({
      ...c,
      costCenterName: patchCc(effectiveSolicitacaoCostCenterId(c), c.costCenterName),
    })),
    cotacoes: data.cotacoes.map((c) => ({
      ...c,
      costCenterName: patchCc(c.costCenterId, c.costCenterName),
    })),
    pedidosSemFinanceiro: data.pedidosSemFinanceiro.map((p) => ({
      ...p,
      costCenterName: patchCc(p.costCenterId, p.costCenterName),
    })),
    pedidosGeradoFinanceiro: data.pedidosGeradoFinanceiro.map((p) => ({
      ...p,
      costCenterName: patchCc(p.costCenterId, p.costCenterName),
    })),
  };
}

function SolicitacaoKanbanCard({
  c,
  onVerItens,
}: {
  c: SolicitacaoCard;
  onVerItens: () => void;
}) {
  const n = c.itemCount ?? 0;
  const obraLine = buildSolicitacaoObraLine(c);
  const ccText = (c.costCenterName ?? "").trim();
  /** Quando não há obra textual, o mock usa o nome do centro de custo nesta faixa. */
  const obraSectionValue = obraLine !== "Obra —" ? obraLine : ccText || "—";
  const itemBadge = n === 1 ? "1 item" : `${formatNumber(n)} itens`;

  return (
    <div className="bg-white rounded-xl border border-slate-200/90 shadow-sm p-3 text-left flex flex-col gap-3 min-h-0">
      <div className="grid grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-violet-50 border border-violet-100 flex-shrink-0">
          <FileText className="w-4 h-4 text-violet-700" strokeWidth={2} aria-hidden />
        </div>
        <p className="min-w-0 text-center text-[15px] sm:text-base font-bold text-violet-700 tabular-nums leading-tight truncate px-0.5">
          SC {c.purchaseRequestId}
        </p>
        <span className="justify-self-end text-[10px] font-semibold text-violet-900 bg-violet-100 px-2 py-1 rounded-full border border-violet-200/70 tabular-nums whitespace-nowrap">
          {itemBadge}
        </span>
      </div>

      <div className="h-px bg-slate-200/90" role="presentation" />

      <div className="flex gap-2.5">
        <Home className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 leading-tight">Obra</p>
          <p className="text-xs font-semibold text-slate-800 mt-0.5 line-clamp-2 leading-snug" title={obraSectionValue}>
            {obraSectionValue}
          </p>
        </div>
      </div>

      <div className="h-px bg-slate-200/90" role="presentation" />

      <div className="flex gap-2.5">
        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 leading-tight">Data</p>
          <p className="text-xs font-semibold text-slate-800 mt-0.5 tabular-nums">{formatIsoDateBr(c.requestDate)}</p>
        </div>
      </div>

      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-full mt-auto h-9 text-[11px] border-violet-200/90 bg-violet-50/90 text-violet-900 hover:bg-violet-100"
        onClick={onVerItens}
        leftIcon={<ListTree className="w-3.5 h-3.5 text-violet-600" />}
      >
        Ver itens
      </Button>
    </div>
  );
}

function CotacaoKanbanCard({ c, onVerItens }: { c: CotacaoCard; onVerItens: () => void }) {
  const n = c.itemCount ?? 0;
  const itemBadge = n === 1 ? "1 item" : `${formatNumber(n)} itens`;
  const supplier = (c.supplierName ?? "").trim();
  const qId = String(c.purchaseQuotationId ?? "").trim();

  return (
    <div className="bg-white rounded-xl border border-slate-200/90 shadow-sm p-3 text-left flex flex-col gap-3 min-h-0">
      <div className="grid grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-50 border border-amber-100 flex-shrink-0">
          <FileSpreadsheet className="w-4 h-4 text-amber-800" strokeWidth={2} aria-hidden />
        </div>
        <p className="min-w-0 text-center text-[15px] sm:text-base font-bold text-amber-900 tabular-nums leading-tight truncate px-0.5">
          CT {qId || "—"}
        </p>
        <span className="justify-self-end text-[10px] font-semibold text-amber-950 bg-amber-100 px-2 py-1 rounded-full border border-amber-200/70 tabular-nums whitespace-nowrap">
          {itemBadge}
        </span>
      </div>

      <div className="h-px bg-slate-200/90" role="presentation" />

      {supplier ? (
        <>
          <div className="flex gap-2.5">
            <User className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-slate-500 leading-tight">Fornecedor</p>
              <p className="text-xs font-semibold text-slate-800 mt-0.5 line-clamp-2 leading-snug" title={supplier}>
                {supplier}
              </p>
            </div>
          </div>

          <div className="h-px bg-slate-200/90" role="presentation" />
        </>
      ) : null}

      <div className="flex gap-2.5">
        <Tag className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 leading-tight">Status</p>
          <span className="mt-1 inline-flex max-w-full items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-900">
            <span className="truncate leading-snug">{c.status || "—"}</span>
          </span>
        </div>
      </div>

      <div className="h-px bg-slate-200/90" role="presentation" />

      <div className="flex gap-2.5">
        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 leading-tight">Data</p>
          <p className="text-xs font-semibold text-slate-800 mt-0.5 tabular-nums">{formatIsoDateBr(c.date)}</p>
        </div>
      </div>

      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-full mt-auto h-9 text-[11px] border-amber-200/90 bg-amber-50/90 text-amber-950 hover:bg-amber-100"
        onClick={onVerItens}
        leftIcon={<ListTree className="w-3.5 h-3.5 text-amber-800" />}
      >
        Ver itens
      </Button>
    </div>
  );
}

function isFullyDeliveredRawStatus(status: string): boolean {
  return /^fully[_-]?delivered$/i.test((status ?? "").trim());
}

/** Chip de pagamento (contas a pagar / outcome) — só rótulos conhecidos do backend Kanban. */
function pedidoPagamentoChipClassName(status: string): string {
  const s = status.trim();
  if (s === "Pago") return "bg-green-100 text-green-900 border border-green-200/80";
  if (s === "Em aberto") return "bg-amber-100 text-amber-950 border border-amber-200/80";
  if (s === "Sem parcelas no período") return "bg-slate-100 text-slate-700 border border-slate-200/70";
  if (s === "Gerado (sem título no retorno)") return "bg-orange-50 text-orange-900 border border-orange-200/70";
  return "bg-slate-100 text-slate-700 border border-slate-200/70";
}

function PedidoKanbanCard({
  c,
  onVerItens,
  hideFullyDeliveredStatusChip,
}: {
  c: PedidoCard;
  onVerItens: () => void;
  hideFullyDeliveredStatusChip?: boolean;
}) {
  const raw = (c.status ?? "").trim();
  const showGreyStatus =
    raw &&
    !(hideFullyDeliveredStatusChip && isFullyDeliveredRawStatus(raw));
  const ccLine = (c.costCenterName ?? "").trim() || "Centro de custo —";
  const paymentLine = (c.accountsPayablePaymentStatus ?? "").trim();
  const showPaymentChip = Boolean(paymentLine && paymentLine !== "—");
  const hasSituationChips =
    showGreyStatus || c.hasFinancialGenerated || c.received || showPaymentChip;

  return (
    <div className="bg-white rounded-xl border border-slate-200/90 shadow-sm p-3 text-left flex flex-col gap-3 min-h-0">
      <div className="grid grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100 border border-slate-200/80 flex-shrink-0">
          <Package className="w-4 h-4 text-slate-700" strokeWidth={2} aria-hidden />
        </div>
        <p className="min-w-0 text-center text-[15px] sm:text-base font-bold text-slate-800 leading-tight truncate px-0.5 tabular-nums">
          Pedido {c.orderNumber}
        </p>
      </div>

      <div className="h-px bg-slate-200/90" role="presentation" />

      <div className="flex gap-2.5">
        <Store className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 leading-tight">Fornecedor</p>
          <p className="text-xs font-semibold text-slate-800 mt-0.5 line-clamp-2 leading-snug">{c.supplier || "Fornecedor —"}</p>
        </div>
      </div>

      <div className="h-px bg-slate-200/90" role="presentation" />

      <div className="flex gap-2.5">
        <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 leading-tight">Centro de custo</p>
          <p className="text-xs font-semibold text-slate-800 mt-0.5 line-clamp-2 leading-snug" title={ccLine}>
            {ccLine}
          </p>
        </div>
      </div>

      <div className="h-px bg-slate-200/90" role="presentation" />

      <div className="flex gap-2.5">
        <Banknote className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 leading-tight">Valor</p>
          <p className="text-xs font-bold text-slate-900 mt-0.5 tabular-nums">{formatCurrency(c.value ?? 0)}</p>
        </div>
      </div>

      {hasSituationChips ? (
        <>
          <div className="h-px bg-slate-200/90" role="presentation" />
          <div className="flex gap-2.5">
            <Tag className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" strokeWidth={1.75} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-slate-500 leading-tight">Situação</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {showGreyStatus ? (
                  <span className="max-w-full truncate rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                    {labelPedidoSiengeStatus(raw)}
                  </span>
                ) : null}
                {c.hasFinancialGenerated ? (
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-900">
                    Financeiro
                  </span>
                ) : null}
                {c.received ? (
                  <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-900">Recebido</span>
                ) : null}
                {showPaymentChip ? (
                  <span
                    className={cn(
                      "max-w-full truncate rounded-full px-2.5 py-1 text-xs font-semibold",
                      pedidoPagamentoChipClassName(paymentLine)
                    )}
                    title={paymentLine}
                  >
                    {paymentLine}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-full mt-auto h-9 text-[11px] border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100"
        onClick={onVerItens}
        leftIcon={<ListTree className="w-3.5 h-3.5 text-brand-600" />}
      >
        Ver itens
      </Button>
    </div>
  );
}

interface ColumnProps {
  title: string;
  icon: ReactNode;
  count: number;
  accent: string;
  children: ReactNode;
}

function KanbanColumn({ title, icon, count, accent, children }: ColumnProps) {
  return (
    <div
      className={cn(
        "flex flex-col min-h-0 min-w-[150px] flex-1 basis-0 rounded-xl border border-slate-200/80 bg-slate-100/90 overflow-hidden"
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 px-2.5 py-2 border-b border-slate-200/80 flex-shrink-0",
          accent
        )}
      >
        <span className="text-slate-600 flex-shrink-0">{icon}</span>
        <h3 className="text-[10px] sm:text-[11px] font-bold text-slate-800 leading-tight flex-1 min-w-0">{title}</h3>
        <span className="text-[10px] font-bold tabular-nums bg-white/80 text-slate-700 px-1.5 py-0.5 rounded-full border border-slate-200/60 flex-shrink-0">
          {count}
        </span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-2 space-y-2 [scrollbar-gutter:stable]">
        {children}
      </div>
    </div>
  );
}

function formatClockTime(ms: number): string {
  return new Date(ms).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function SuprimentosKanbanPage() {
  const navigate = useNavigate();
  const { suprimentos, ccOptions, loadingCC, ccNameMap } = useModules();
  const {
    values,
    setValues,
    apply,
    applyWith,
    reset,
    autoLoad,
    data,
    loading,
    error,
    supplierOptions,
    itemOptions,
    loadingItems,
    applied,
  } = suprimentos;
  const [scModal, setScModal] = useState<SolicitacaoCard | null>(null);
  const [cotacaoModal, setCotacaoModal] = useState<CotacaoCard | null>(null);
  const [pedidoModal, setPedidoModal] = useState<PedidoCard | null>(null);
  const kambarInitDone = useRef(false);

  const {
    secondsUntilRefresh,
    lastRefreshedAt,
    triggerRefresh,
    refreshIntervalMinutes,
  } = useKanbanAutoRefresh({
    apply,
    loading,
    hasData: data != null,
  });

  const kambarCcIds = useMemo(() => findKambarCostCenterIds(ccOptions), [ccOptions]);
  const kambarActive = kambarCcIds.length > 0;

  /* Carga inicial: filtra Kambar assim que os centros de custo estiverem disponíveis. */
  useEffect(() => {
    if (loadingCC || kambarInitDone.current) return;
    kambarInitDone.current = true;
    if (kambarCcIds.length > 0) {
      applyWith({ ...values, costCenter: kambarCcIds });
    } else {
      autoLoad();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- carga inicial única ao abrir o Kanban
  }, [loadingCC, kambarCcIds]);

  /**
   * Filtro do quadro: SC / Cotação / Pedido reagem digitando; datas, CC e fornecedor
   * usam o último «Aplicar» (`applied`) até nova aplicação.
   */
  const kanbanFilterValues = useMemo(
    () => ({
      ...applied,
      quotationFilter:        values.quotationFilter ?? "",
      purchaseRequestFilter:  values.purchaseRequestFilter ?? "",
      orderFilter:            values.orderFilter ?? "",
    }),
    [
      applied,
      values.quotationFilter,
      values.purchaseRequestFilter,
      values.orderFilter,
    ],
  );

  const board = useMemo(() => {
    if (!data) return null;
    const enriched = enrichKanbanData(data, ccOptions, ccNameMap);
    return filterKanbanComprasData(enriched, kanbanFilterValues);
  }, [data, ccOptions, ccNameMap, kanbanFilterValues]);

  const isFirstLoad = loading && !data;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2 px-3 py-2 md:px-4 md:py-3 overflow-hidden">
      {scModal && (
        <SolicitacaoItensModal
          solicitacao={scModal}
          onClose={() => setScModal(null)}
          itemQueryPeriod={{ startDate: applied.startDate, endDate: applied.endDate }}
        />
      )}
      {cotacaoModal && <CotacaoItensModal cotacao={cotacaoModal} onClose={() => setCotacaoModal(null)} />}
      {pedidoModal && <PedidoItensModal pedido={pedidoModal} onClose={() => setPedidoModal(null)} />}

      <FilterBar
        values={values}
        onChange={setValues}
        onApply={apply}
        onReset={reset}
        costCenterOptions={ccOptions}
        loadingOptions={loadingCC}
        loading={loading}
        className="py-3 flex-shrink-0"
        showSuprimentosEntityFilters
        showSuprimentosKanbanDocFilters
        supplierOptions={supplierOptions}
        loadingSupplierOptions={loading}
        itemOptions={itemOptions}
        loadingItemOptions={loadingItems}
        headerEnd={
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex flex-wrap items-center justify-end gap-2 text-[11px] text-slate-500">
              {kambarActive && (
                <span className="font-semibold text-violet-800 max-w-[200px] truncate" title={kambarFilterLabel(ccOptions, kambarCcIds)}>
                  {kambarFilterLabel(ccOptions, kambarCcIds)}
                </span>
              )}
              {kambarActive && <span className="text-slate-300" aria-hidden>·</span>}
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 tabular-nums",
                  loading
                    ? "border-violet-200 bg-violet-50/80 text-violet-800"
                    : "border-slate-200 bg-white text-slate-600"
                )}
                title={`Atualização automática a cada ${refreshIntervalMinutes} minutos`}
              >
                <Timer className="w-3.5 h-3.5 flex-shrink-0 text-violet-600" aria-hidden />
                {loading ? (
                  <span className="font-medium">Atualizando…</span>
                ) : secondsUntilRefresh != null ? (
                  <>
                    <span className="text-slate-500">Próxima em</span>
                    <span className="font-bold text-violet-800 min-w-[2.75rem] text-center">
                      {formatKanbanCountdown(secondsUntilRefresh)}
                    </span>
                  </>
                ) : (
                  <span className="text-slate-400">Aguardando dados…</span>
                )}
              </span>
              {lastRefreshedAt != null && (
                <span className="text-slate-400 whitespace-nowrap">
                  Última {formatClockTime(lastRefreshedAt)}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={triggerRefresh}
              loading={loading}
              leftIcon={<RefreshCw className="w-4 h-4" />}
              className="text-slate-600"
            >
              Atualizar
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={() => navigate(ROUTES.SUPRIMENTOS)}
              leftIcon={<BarChart3 className="w-4 h-4" />}
            >
              Visão em Gráficos
            </Button>
            </div>
          </div>
        }
      />

      {error && (
        <div className="flex-shrink-0 flex items-center gap-3 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="min-w-0">{error}</span>
          <button type="button" onClick={triggerRefresh} className="ml-auto text-red-500 hover:text-red-700 underline text-xs whitespace-nowrap">
            Tentar novamente
          </button>
        </div>
      )}

      {isFirstLoad && (
        <p className="text-sm text-slate-500 text-center py-2 flex-shrink-0">
          Consultando o Sienge… isso pode levar alguns segundos.
        </p>
      )}

      {board && !isFirstLoad && (
        <div className="flex flex-1 min-h-0 gap-2 overflow-x-auto overflow-y-hidden pb-1">
          <KanbanColumn
            title="Solicitações pendentes de aprovação Suprimentos"
            count={board.solPendente.length}
            accent="bg-violet-100/90"
            icon={<ClipboardList className="w-4 h-4" />}
          >
            {board.solPendente.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center py-6 px-1">Nenhuma solicitação nesta etapa.</p>
            ) : (
              board.solPendente.map((c) => (
                <SolicitacaoKanbanCard key={c.purchaseRequestId} c={c} onVerItens={() => setScModal(c)} />
              ))
            )}
          </KanbanColumn>

          <KanbanColumn
            title="Solicitações pendentes de aprovação Gestor"
            count={board.solAprovada.length}
            accent="bg-indigo-100/90"
            icon={<Building2 className="w-4 h-4" />}
          >
            {board.solAprovada.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center py-6 px-1">Nenhuma solicitação nesta etapa.</p>
            ) : (
              board.solAprovada.map((c) => (
                <SolicitacaoKanbanCard key={c.purchaseRequestId} c={c} onVerItens={() => setScModal(c)} />
              ))
            )}
          </KanbanColumn>

          <KanbanColumn
            title="Cotações"
            count={board.cotacoes.length}
            accent="bg-amber-100/90"
            icon={<FileSpreadsheet className="w-4 h-4" />}
          >
            {board.cotacoes.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center py-6 px-1">Nenhuma cotação no período.</p>
            ) : (
              board.cotacoes.map((c) => (
                <CotacaoKanbanCard key={c.purchaseQuotationId} c={c} onVerItens={() => setCotacaoModal(c)} />
              ))
            )}
          </KanbanColumn>

          <KanbanColumn
            title="Pedidos pendentes de entrega"
            count={board.pedidosSemFinanceiro.length}
            accent="bg-orange-100/90"
            icon={<Truck className="w-4 h-4" />}
          >
            {board.pedidosSemFinanceiro.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center py-6 px-1">Nenhum pedido sem financeiro.</p>
            ) : (
              board.pedidosSemFinanceiro.map((c) => (
                <PedidoKanbanCard key={c.purchaseOrderId} c={c} onVerItens={() => setPedidoModal(c)} />
              ))
            )}
          </KanbanColumn>

          <KanbanColumn
            title="Gerado financeiro"
            count={board.pedidosGeradoFinanceiro.length}
            accent="bg-emerald-100/90"
            icon={<Package className="w-4 h-4" />}
          >
            {board.pedidosGeradoFinanceiro.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center py-6 px-1">Nenhum pedido nesta coluna.</p>
            ) : (
              board.pedidosGeradoFinanceiro.map((c) => (
                <PedidoKanbanCard
                  key={c.purchaseOrderId}
                  c={c}
                  onVerItens={() => setPedidoModal(c)}
                  hideFullyDeliveredStatusChip
                />
              ))
            )}
          </KanbanColumn>
        </div>
      )}
    </div>
  );
}
